import { useEffect, useState } from 'react';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { LoanReviewCrossCheckRow } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { displayStatusLabel } from '../../lib/loanReview/mapLoanReviewToViewModel';
import { statusChipColor } from './loanReviewChipColors';
import { ApplicationSectionCard } from '../application/applicationSectionChrome';

/**
 * What an empty cell says.
 *
 * A dash means this source is not part of this comparison — the Employer column on a
 * date-of-birth row was never going to hold anything. "Unavailable" means the source
 * IS part of it and returned nothing, which is a fact the reviewer needs: a blank
 * Bureau column on the legal-name row is the bureau not answering, and it used to be
 * indistinguishable from a column that simply does not apply.
 */
function emptyCellLabel(
  row: LoanReviewCrossCheckRow,
  key: 'identity' | 'income' | 'employer' | 'bureau',
): string {
  return row.asked?.includes(key) ? 'Unavailable' : '—';
}

const COLUMNS: Array<{ key: 'identity' | 'income' | 'employer' | 'bureau'; label: string }> = [
  { key: 'identity', label: 'Identity (MiTek)' },
  { key: 'income', label: 'Bank / Income (Flinks)' },
  { key: 'employer', label: 'Employer' },
  { key: 'bureau', label: 'Bureau (TransUnion)' },
];

/** Keep scrolled rows below sticky Layout top bar + Application tab bar. */
const STICKY_SCROLL_MARGIN = {
  xs: '5.5rem',
  md: 'calc(var(--admin-topbar-height, 52px) + 5.5rem)',
} as const;

type Props = {
  rows: LoanReviewCrossCheckRow[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  highlightField?: string | null;
  /** Incremented on each jump click so the same field re-scrolls. */
  jumpNonce?: number;
};

/** Collapsed-by-default table: each shared field's value across every source. */
export function LoanReviewConsistencyMatrix({
  rows,
  open: openProp,
  onOpenChange,
  highlightField = null,
  jumpNonce = 0,
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;

  const setOpen = (next: boolean | ((prev: boolean) => boolean)) => {
    const value = typeof next === 'function' ? next(open) : next;
    if (!controlled) setInternalOpen(value);
    onOpenChange?.(value);
  };

  useEffect(() => {
    if (!open || jumpNonce < 1) return;
    const id = highlightField
      ? `loan-review-matrix-row-${highlightField.toLowerCase().replace(/\s+/g, '-')}`
      : 'loan-review-consistency-matrix';
    // Wait for Collapse to mount the table before scrolling into view.
    const timer = window.setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 280);
    return () => window.clearTimeout(timer);
  }, [highlightField, open, jumpNonce]);

  if (!rows.length) return null;

  // Show a source column when any row has a value for it, OR when any row asked that
  // source and it stayed silent. Filtering on values alone made a source that was
  // consulted and returned nothing disappear from the table completely, which reads as
  // "we never looked" — the same ambiguity as a blank cell, one level up.
  const activeColumns = COLUMNS.filter((c) =>
    rows.some((r) => r[c.key] || r.asked?.includes(c.key)),
  );

  return (
    <Box id="loan-review-consistency-matrix" sx={{ scrollMarginTop: STICKY_SCROLL_MARGIN }}>
    <ApplicationSectionCard
      title="Cross-source comparison"
      subtitle="Side-by-side values from each source, for confirming identity"
      icon={<FactCheckRoundedIcon fontSize="small" />}
      data-testid="loan-review-consistency-matrix"
      action={
        <Button
          size="small"
          variant="text"
          onClick={() => setOpen((v) => !v)}
          endIcon={
            <ExpandMoreRoundedIcon
              fontSize="small"
              sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}
            />
          }
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {open ? 'Hide' : 'Show'}
        </Button>
      }
    >
      <Collapse in={open} unmountOnExit>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Cross-source comparison">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                {activeColumns.map((c) => (
                  <TableCell key={c.key} sx={{ fontWeight: 700 }}>
                    {c.label}
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const highlighted =
                  Boolean(highlightField) &&
                  row.compare.toLowerCase() === (highlightField || '').toLowerCase();
                const rowId = `loan-review-matrix-row-${row.compare.toLowerCase().replace(/\s+/g, '-')}`;
                return (
                  <TableRow
                    key={row.matchType}
                    id={rowId}
                    hover
                    selected={highlighted}
                    sx={{
                      scrollMarginTop: STICKY_SCROLL_MARGIN,
                      ...(highlighted
                        ? {
                            bgcolor: 'action.selected',
                            '& td': { borderBottomColor: 'warning.light' },
                          }
                        : {}),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{row.compare}</TableCell>
                    {activeColumns.map((c) => (
                      <TableCell key={c.key} sx={{ color: row[c.key] ? 'text.primary' : 'text.disabled' }}>
                        {row[c.key] ?? emptyCellLabel(row, c.key)}
                      </TableCell>
                    ))}
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusChipColor(row.status)}
                        label={displayStatusLabel(row.status)}
                        sx={{ fontWeight: 700, height: 20 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Collapse>
      {!open ? (
        <Typography variant="body2" color="text.secondary">
          {rows.length} field{rows.length === 1 ? '' : 's'} compared across sources.
        </Typography>
      ) : null}
    </ApplicationSectionCard>
    </Box>
  );
}
